import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { db, getAdminAuthHeaders } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

/**
 * Coverage Tab — operational view of the coverage guarantee.
 *
 * Sources:
 *   GET /getCoverageStatus (admin)  - refresh ledger stale entries + SLAs +
 *                                     last coverage watchdog run
 *   _refresh_health/alert_coverage  - critical SLA-breach alert (3× SLA)
 *   _refresh_retry_queue/*          - retry queue depth per refresh job
 *   app_config/archive_mode         - freeze mode (staleness is EXPECTED when on)
 *
 * Complements RefreshHealthTab: that tab shows per-tier run stats; this tab
 * answers "is any league falling out of coverage, and is the watchdog alive?"
 */

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
);

const RETRY_KEYS = [
  { key: 'main_refresh',  label: 'Main (Tier 1)' },
  { key: 'tiered_TIER2',  label: 'Tier 2' },
  { key: 'tiered_TIER3A', label: 'Tier 3A' },
  { key: 'tiered_TIER3B', label: 'Tier 3B' },
];

function tsToMs(ts) {
  if (!ts) return null;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts === 'object' && typeof ts._seconds === 'number') return ts._seconds * 1000;
  if (typeof ts === 'object' && typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (typeof ts === 'string') { const ms = new Date(ts).getTime(); return Number.isFinite(ms) ? ms : null; }
  if (typeof ts === 'number') return ts > 1e12 ? ts : ts * 1000;
  return null;
}

function formatAge(ms) {
  if (ms === null || !Number.isFinite(ms)) return 'never';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatHours(ms) {
  if (!Number.isFinite(ms)) return '—';
  return `${(ms / 3600000).toFixed(1)}h`;
}

const KIND_META = {
  core:        { label: 'Core',  color: '#22c55e', icon: 'flash-outline' },
  props:       { label: 'Props', color: '#3b82f6', icon: 'person-outline' },
  derivatives: { label: 'Deriv', color: '#a855f7', icon: 'git-branch-outline' },
};

const CoverageTab = () => {
  const [status, setStatus] = useState(null);       // getCoverageStatus payload
  const [statusError, setStatusError] = useState(null);
  const [alertDoc, setAlertDoc] = useState(null);   // _refresh_health/alert_coverage
  const [retryData, setRetryData] = useState({});
  const [freeze, setFreeze] = useState(null);       // app_config/archive_mode
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      // 1. Coverage status endpoint (admin-authed)
      try {
        const headers = await getAdminAuthHeaders();
        const resp = await fetch(`${getBase()}/getCoverageStatus`, { headers });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setStatus(await resp.json());
        setStatusError(null);
      } catch (e) {
        setStatus(null);
        setStatusError(e?.message || String(e));
      }

      // 2. Escalation alert + freeze mode + retry queues (direct Firestore)
      const [alertSnap, freezeSnap] = await Promise.all([
        getDoc(doc(db, '_refresh_health', 'alert_coverage')).catch(() => null),
        getDoc(doc(db, 'app_config', 'archive_mode')).catch(() => null),
      ]);
      setAlertDoc(alertSnap?.exists?.() ? alertSnap.data() : null);
      setFreeze(freezeSnap?.exists?.() ? freezeSnap.data() : null);

      const retryResults = {};
      await Promise.all(RETRY_KEYS.map(async (r) => {
        try {
          const snap = await getDoc(doc(db, '_refresh_retry_queue', r.key));
          retryResults[r.key] = snap.exists() ? snap.data() : null;
        } catch { retryResults[r.key] = null; }
      }));
      setRetryData(retryResults);
    } catch (e) {
      console.error('[CoverageTab] load error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  const onRefresh = () => { setRefreshing(true); loadAll(); };

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator size="large" color="#6366f1" />
        <Text style={s.loadingText}>Loading coverage status…</Text>
      </View>
    );
  }

  const freezeOn = freeze?.freeze_mode === true;
  const stale = Array.isArray(status?.stale) ? status.stale : [];
  const staleCount = status?.staleCount ?? stale.length;
  const escalated = stale.filter((e) => e.escalate);
  const watchdog = status?.lastWatchdogRun || null;
  const watchdogAge = watchdog
    ? Date.now() - (tsToMs(watchdog.timestamp || watchdog.updatedAt || watchdog.finishedAt) ?? NaN)
    : null;

  // Retry queue docs are deleted on success — existence means an active backlog.
  // Shape (oddsRefresh.recordSkippedForRetry): { refreshType, skippedLeagues,
  // skippedFixtures, timestamp, retryCount }.
  const retryDepth = RETRY_KEYS.reduce((sum, r) => {
    const d = retryData[r.key];
    if (!d) return sum;
    return sum + (Array.isArray(d.skippedLeagues) ? d.skippedLeagues.length : 1);
  }, 0);

  // Freeze mode makes staleness expected — treat it as informational, not red.
  const healthy = freezeOn || (staleCount === 0 && !alertDoc && retryDepth === 0);

  return (
    <ScrollView
      style={s.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#6366f1" />}
    >
      {/* Header */}
      <View style={s.header}>
        <View style={s.headerLeft}>
          <Ionicons name="shield-checkmark-outline" size={22} color="#6366f1" />
          <Text style={s.headerTitle}>Coverage Guarantee</Text>
        </View>
        <TouchableOpacity style={s.refreshBtn} onPress={onRefresh}>
          <Ionicons name="refresh-outline" size={16} color="#6366f1" />
          <Text style={s.refreshBtnText}>Refresh</Text>
        </TouchableOpacity>
      </View>
      <Text style={s.pointer}>
        This tab watches refresh SLAs and the watchdog. To probe whether a sportsbook posted a specific game, use Labs → Coverage Lab.
      </Text>

      {/* Freeze notice */}
      {freezeOn && (
        <View style={[s.banner, { borderColor: '#38bdf8', backgroundColor: '#082f4933' }]}>
          <Ionicons name="snow-outline" size={20} color="#38bdf8" />
          <View style={s.bannerText}>
            <Text style={[s.bannerTitle, { color: '#38bdf8' }]}>Archive / Freeze Mode Active</Text>
            <Text style={s.bannerSub}>
              Frozen at {freeze?.freeze_time || 'unknown'} — refreshes and the watchdog are intentionally
              paused, so stale ledger entries below are expected and NOT a coverage failure.
            </Text>
          </View>
        </View>
      )}

      {/* Overall banner */}
      <View style={[s.banner, { borderColor: healthy ? '#22c55e' : '#ef4444', backgroundColor: healthy ? '#052e1633' : '#3b000a33' }]}>
        <Ionicons name={healthy ? 'checkmark-circle' : 'warning'} size={20} color={healthy ? '#22c55e' : '#ef4444'} />
        <View style={s.bannerText}>
          <Text style={[s.bannerTitle, { color: healthy ? '#22c55e' : '#ef4444' }]}>
            {freezeOn ? 'Coverage Paused (Frozen)'
              : healthy ? 'Full Coverage — No Stale Leagues'
              : `${staleCount} Stale League(s)${alertDoc ? ' · SLA BREACH ALERT' : ''}`}
          </Text>
          {statusError && (
            <Text style={s.bannerSub}>getCoverageStatus failed: {statusError}</Text>
          )}
        </View>
      </View>

      {/* Stat cards */}
      <View style={s.statRow}>
        <View style={s.statCard}>
          <Text style={s.statValue}>{status ? staleCount : '—'}</Text>
          <Text style={s.statLabel}>Stale leagues</Text>
        </View>
        <View style={s.statCard}>
          <Text style={[s.statValue, escalated.length > 0 && { color: '#ef4444' }]}>{status ? escalated.length : '—'}</Text>
          <Text style={s.statLabel}>Beyond 3× SLA</Text>
        </View>
        <View style={s.statCard}>
          <Text style={[s.statValue, retryDepth > 0 && { color: '#f59e0b' }]}>{retryDepth}</Text>
          <Text style={s.statLabel}>Retry queue depth</Text>
        </View>
        <View style={s.statCard}>
          <Text style={s.statValue}>{watchdog ? formatAge(watchdogAge) : 'never'}</Text>
          <Text style={s.statLabel}>Last watchdog run</Text>
        </View>
      </View>

      {/* SLAs */}
      {status?.slas && (
        <View style={s.slaRow}>
          <Ionicons name="timer-outline" size={13} color="rgba(255,255,255,0.4)" />
          <Text style={s.slaText}>
            SLAs — {Object.entries(status.slas).map(([k, v]) => `${k}: ${formatHours(v)}`).join(' · ')}
            {'  '}(escalation at 3×)
          </Text>
        </View>
      )}

      {/* Escalation alert detail */}
      {alertDoc && (
        <View style={s.alertCard}>
          <View style={s.alertHeader}>
            <Ionicons name="alert-circle" size={16} color="#ef4444" />
            <Text style={s.alertTitle}>
              Critical: {alertDoc.count} league(s) beyond 3× SLA
            </Text>
          </View>
          {(alertDoc.entries || []).map((e, i) => (
            <Text key={i} style={s.alertEntry}>
              {e.kind}/{e.sport}/{e.league} — {e.hoursStale}h stale, {e.consecutiveMisses ?? '?'} consecutive misses
            </Text>
          ))}
        </View>
      )}

      {/* Watchdog last run detail */}
      {watchdog && (
        <View style={s.card}>
          <View style={s.cardHeader}>
            <Ionicons name="paw-outline" size={15} color="#6366f1" />
            <Text style={s.cardTitle}>Coverage Watchdog</Text>
          </View>
          <View style={s.kvRow}><Text style={s.kvKey}>Last run</Text><Text style={s.kvVal}>{formatAge(watchdogAge)}</Text></View>
          {watchdog.staleCount != null && (
            <View style={s.kvRow}><Text style={s.kvKey}>Stale found</Text><Text style={s.kvVal}>{watchdog.staleCount} ({watchdog.escalationCount ?? 0} escalated)</Text></View>
          )}
          {Array.isArray(watchdog.rescues) && (
            <View style={s.kvRow}>
              <Text style={s.kvKey}>Rescues</Text>
              <Text style={s.kvVal}>
                {watchdog.rescues.length === 0
                  ? 'none needed'
                  : `${watchdog.rescuesSucceeded ?? watchdog.rescues.filter(r => r.ok).length}/${watchdog.rescues.length} OK — ${watchdog.rescues.map(r => r.target).filter(Boolean).join(', ')}`}
              </Text>
            </View>
          )}
          {watchdog.durationMs != null && (
            <View style={s.kvRow}><Text style={s.kvKey}>Duration</Text><Text style={s.kvVal}>{(watchdog.durationMs / 1000).toFixed(0)}s</Text></View>
          )}
        </View>
      )}

      {/* Retry queues */}
      <View style={s.card}>
        <View style={s.cardHeader}>
          <Ionicons name="repeat-outline" size={15} color="#f59e0b" />
          <Text style={s.cardTitle}>Retry Queues</Text>
        </View>
        {RETRY_KEYS.map((r) => {
          const d = retryData[r.key];
          const leagues = Array.isArray(d?.skippedLeagues) ? d.skippedLeagues : [];
          const active = !!d;
          return (
            <View key={r.key} style={s.kvRow}>
              <Text style={s.kvKey}>{r.label}</Text>
              <Text style={[s.kvVal, active ? { color: '#f59e0b' } : { color: '#22c55e' }]}>
                {active
                  ? `${leagues.length || '?'} queued (retry #${d.retryCount ?? 0})${leagues.length ? `: ${leagues.slice(0, 6).join(', ')}${leagues.length > 6 ? '…' : ''}` : ''}`
                  : 'empty'}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Stale league table */}
      <View style={s.card}>
        <View style={s.cardHeader}>
          <Ionicons name="list-outline" size={15} color={uiVar('adm-accent', '#a855f7')} />
          <Text style={s.cardTitle}>Stale Ledger Entries {status ? `(${staleCount})` : ''}</Text>
        </View>
        {!status && (
          <Text style={s.emptyText}>Unavailable — {statusError || 'endpoint error'}</Text>
        )}
        {status && stale.length === 0 && (
          <Text style={[s.emptyText, { color: '#22c55e' }]}>
            Every league is inside its refresh SLA. Comprehensive coverage intact.
          </Text>
        )}
        {stale.map((e, i) => {
          const meta = KIND_META[e.kind] || { label: e.kind, color: '#94a3b8', icon: 'help-outline' };
          return (
            <View key={`${e.kind}-${e.sport}-${e.league}-${i}`} style={s.staleRow}>
              <View style={[s.kindBadge, { borderColor: meta.color, backgroundColor: meta.color + '22' }]}>
                <Text style={[s.kindBadgeText, { color: meta.color }]}>{meta.label}</Text>
              </View>
              <Text style={s.staleLeague}>{e.sport}/{e.league}</Text>
              <Text style={[s.staleAge, e.escalate && { color: '#ef4444', fontWeight: '700' }]}>
                {formatHours(e.ageMs)} stale{e.consecutiveMisses ? ` · ${e.consecutiveMisses} misses` : ''}{e.escalate ? ' · 3×SLA' : ''}
              </Text>
            </View>
          );
        })}
      </View>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

const s = StyleSheet.create({
  container: { flex: 1, padding: 16 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  loadingText: { color: 'rgba(255,255,255,0.5)', marginTop: 10, fontSize: 13 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerTitle: { ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal'), color: uiVar('adm-ink', '#fff'), fontSize: 17, fontWeight: '700' },
  pointer: { color: 'rgba(255,255,255,0.42)', fontSize: 12, lineHeight: 17, marginBottom: 14, maxWidth: 640 },
  refreshBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8,
    borderWidth: 1, borderColor: '#6366f155', backgroundColor: '#6366f111',
  },
  refreshBtnText: { color: '#6366f1', fontSize: 12, fontWeight: '600' },

  banner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    borderWidth: 1, borderRadius: 10, padding: 12, marginBottom: 12,
  },
  bannerText: { flex: 1 },
  bannerTitle: { fontSize: 14, fontWeight: '700' },
  bannerSub: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 },

  statRow: { flexDirection: 'row', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  statCard: {
    flex: 1, minWidth: 120,
    backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 10, padding: 12, alignItems: 'center',
  },
  statValue: { color: uiVar('adm-ink', '#fff'), fontSize: 20, fontWeight: '800' },
  statLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 3, textAlign: 'center' },

  slaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12, paddingHorizontal: 2 },
  slaText: { color: 'rgba(255,255,255,0.4)', fontSize: 11, flex: 1 },

  alertCard: {
    borderWidth: 1, borderColor: '#ef4444', backgroundColor: '#3b000a33',
    borderRadius: 10, padding: 12, marginBottom: 12,
  },
  alertHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  alertTitle: { color: '#ef4444', fontSize: 13, fontWeight: '700' },
  alertEntry: { color: 'rgba(255,255,255,0.7)', fontSize: 12, paddingVertical: 1 },

  card: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
    borderRadius: 10, padding: 12, marginBottom: 12,
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 },
  cardTitle: { color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: '700' },

  kvRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)', gap: 10,
  },
  kvKey: { color: 'rgba(255,255,255,0.5)', fontSize: 12 },
  kvVal: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: '600', flexShrink: 1, textAlign: 'right' },

  emptyText: { color: 'rgba(255,255,255,0.4)', fontSize: 12, paddingVertical: 8 },

  staleRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)',
  },
  kindBadge: { borderWidth: 1, borderRadius: 5, paddingHorizontal: 6, paddingVertical: 2 },
  kindBadgeText: { fontSize: 10, fontWeight: '700' },
  staleLeague: { color: uiVar('adm-ink', '#fff'), fontSize: 12.5, fontWeight: '600', flex: 1 },
  staleAge: { color: '#f59e0b', fontSize: 11.5 },
});

export default CoverageTab;
